import React from 'react';

import useEventBusClient from 'pa-react/dist/core/eventBus/useEventBusClient';

/**
 * @typedef EventBusClient
 * @property {string} type - hard coded to '_REACT'
 * @property {string} id - uuid (v1) given to event bus client
 * @property {Function} deregisterEventListenerMap ()
 * @property {Function} registerEventListenerMap ()
 * @property {Function} removePersistedEvents ()
 * @property {Function} sendEvent (eventName: string, data: any, callback: function, sendTo: ??, sendToSender: ??, persistent: ??, overridable: ??)
 */

/**
 * Hook that allows you connect to apps event bus client You can setup listeners and send events
 * with this hook.
 *
 * Takes an optional event listener map object as an argument where keys are event names and values
 * are event handler functions. This is only needed if you want to listen for events.
 *
 * Usage Example 1 (Listener Only)
 * ```
 *     useEventBus({ eventName: () => handleEvent() })
 * ```
 *
 * Usage Example 2 (Send Only)
 * ```
 *     const [eventBusClient] = useEventBus();
 *     ...
 *     eventBusClient.sendEvent("eventName", data, ...);
 * ```
 *
 * Usage Example 3 (Listen and Send)
 * ```
 *     const [eventBusClient] = useEventBus({ sampleEvent: () => handleSampeEvent() });
 *     ...
 *     eventBusClient.sendEvent("differentEvent", differentEventData);
 * ```
 *
 * @param {Object.<string, Function>} eventMap
 * @returns {[ EventBusClient ]} eventBusClient
 */
export function useEventBus(eventMap = {}) {
  const eventBusClient = useEventBusClient({}); // empty object required as param
  const eventListenerMapRef = React.useRef(eventMap);

  React.useEffect(() => {
    // avoid warning about eventListerMapRef possibly changing by storing in locally scope variable
    const eventListenerMap = eventListenerMapRef.current;

    eventBusClient.registerEventListenerMap(eventListenerMap);
    return () => eventBusClient.deregisterEventListenerMap(eventListenerMap);
  }, [eventBusClient]);

  return [eventBusClient];
}
